import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { canonicalUrl } from '@/utils/helpers';

const typeOptions = ['全部', '反转盘点', '录音流出', '聊天记录', '当事人回应', '打脸现场'];

export default function CategoryPage() {
  const { slug = '' } = useParams();
  const cat = categories.find((c) => c.slug === slug) || categories[0];
  const [type, setType] = useState('全部');

  const scoped = useMemo(() => articles.filter((a) => a.cat === cat.slug), [cat.slug]);
  const list = type === '全部' ? scoped : scoped.filter((a) => a.tag === type);
  const count = scoped.length;

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${cat.name}栏目 - 每日大瓜`,
      url: canonicalUrl(),
      inLanguage: 'zh-CN',
      isPartOf: { '@type': 'WebSite', name: '每日大瓜' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
        { '@type': 'ListItem', position: 2, name: cat.name, item: canonicalUrl() },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${cat.name} - ${cat.desc} | 每日大瓜`}
        description={`每日大瓜${cat.name}栏目：${cat.desc}收录全网吃瓜时间线相关内容，瓜中瓜反转、当事人回应、录音与聊天记录证据一网打尽。`}
        keywords={`${cat.name},每日大瓜,全网吃瓜时间线,娱乐瓜讯`}
        image={scoped[0]?.cover}
        jsonLd={schema}
      />
      <SiteHeader />

      <main>
        <div className="mx-auto w-full max-w-6xl px-4 md:px-6 py-6">
          <nav aria-label="面包屑" className="mb-4 flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
            <Link to="/" className="inline-flex items-center gap-1 hover:text-primary-700">
              <i className="ri-home-5-line" aria-hidden="true" /> 首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
            <span className="text-foreground-800">{cat.name}</span>
          </nav>

          <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
            <span className={`mb-3 flex h-12 w-12 items-center justify-center rounded-md text-2xl ${cat.tint}`}>
              <i className={cat.icon} aria-hidden="true" />
            </span>
            <h1 className="text-2xl md:text-3xl font-bold">{cat.name} · 吃瓜前线</h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">{cat.desc}</p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span className="inline-flex items-center gap-1 rounded-full bg-background-50 border border-background-200 px-3 py-1 text-foreground-600">
                <i className="ri-article-line text-primary-600" aria-hidden="true" /> 共 {count} 篇瓜讯
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-background-50 border border-background-200 px-3 py-1 text-foreground-600">
                <i className="ri-refresh-line text-accent-600" aria-hidden="true" /> 持续追踪更新
              </span>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {typeOptions.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition ${
                  type === t ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:text-primary-700'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <section className="mx-auto w-full max-w-6xl px-4 md:px-6 pb-12">
          {list.length > 0 ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-background-200 bg-background-50 py-16 text-center">
              <i className="ri-inbox-2-line text-4xl text-background-300" aria-hidden="true" />
              <p className="mt-3 text-sm text-foreground-600">该分类下暂无此类瓜讯，换个类型看看吧</p>
            </div>
          )}
        </section>
      </main>

      <SiteFooter />
    </>
  );
}